import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  HTTP_REQUEST_ERROR_EVENT,
  type HttpRequestErrorDetail
} from '@/constants/httpRequestError';

import { Modal } from './Modal';

const FORBIDDEN_STATUS = 403;
const FORBIDDEN_FALLBACK_MESSAGE = "You're not allowed to take that action.";
const GENERIC_FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Global surface for failed HTTP requests that carry no validation error bag (anything but 422):
 * 403s open a modal explaining the action is not allowed, every other error status raises a toast.
 * Mounted once in the app shell; fed by {@link HTTP_REQUEST_ERROR_EVENT} dispatched from the axios
 * interceptors and Inertia's HTTP client error hook.
 */
export function HttpRequestErrorModal() {
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState(FORBIDDEN_FALLBACK_MESSAGE);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<HttpRequestErrorDetail>).detail;

      if (!detail) {
        return;
      }

      if (detail.status === FORBIDDEN_STATUS) {
        setMessage(detail.message ?? FORBIDDEN_FALLBACK_MESSAGE);
        setShow(true);
        return;
      }

      // Retried requests (e.g. React Query) re-fire the event; a stable id per status collapses
      // them into a single toast.
      toast.error(detail.message ?? GENERIC_FALLBACK_MESSAGE, {
        id: `http-request-error-${detail.status}`
      });
    };

    window.addEventListener(HTTP_REQUEST_ERROR_EVENT, handler);
    return () => window.removeEventListener(HTTP_REQUEST_ERROR_EVENT, handler);
  }, []);

  const handleClose = () => {
    setShow(false);
  };

  return (
    <Modal
      show={show}
      onClose={handleClose}
      title="Action Not Allowed"
      maxWidth="md"
      closeable={false}
      cancelLabel=""
      submitLabel="OK"
      onSubmit={(e) => {
        e.preventDefault();
        handleClose();
      }}
    >
      <p className="text-foreground">{message}</p>
    </Modal>
  );
}
